"use client";

import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type UIEventHandler,
} from "react";

import { cn } from "@/lib/utils";

const OVERLAP_THRESHOLD_PX = 1;

// All scroll-edge shadows share the --scroll-shadow-stops tokens
// (globals.css) so the horizontal table shadows and VerticalScrollEndShadow
// stay one visual system.
//
// The horizontal shadow is painted per sticky cell, so adjacent segments must
// tile into one continuous strip. after:-bottom-px extends each segment over
// its cell's 1px bottom border — with plain inset-y-0 every row border cuts a
// white gap through the shadow.
// Apply start/end unconditionally on the sticky cells and gate only
// `visible` on the overlap state — the pseudo-element must stay mounted with
// a transition for the shadow to fade in/out instead of popping.
export const horizontalScrollShadowClassNames = {
  start:
    "after:pointer-events-none after:absolute after:top-0 after:-bottom-px after:left-full after:w-4 after:bg-[linear-gradient(to_right,var(--scroll-shadow-stops))] after:opacity-0 after:transition-opacity after:duration-150 after:content-['']",
  end: "after:pointer-events-none after:absolute after:top-0 after:-bottom-px after:right-full after:w-4 after:bg-[linear-gradient(to_left,var(--scroll-shadow-stops))] after:opacity-0 after:transition-opacity after:duration-150 after:content-['']",
  visible: "after:opacity-100",
} as const;

export type HorizontalScrollOverlap = {
  start: boolean;
  end: boolean;
};

type HorizontalScrollMetrics = Pick<
  HTMLElement,
  "clientWidth" | "scrollLeft" | "scrollWidth"
>;

export function getHorizontalScrollOverlap(
  metrics: HorizontalScrollMetrics,
): HorizontalScrollOverlap {
  return {
    start: metrics.scrollLeft > OVERLAP_THRESHOLD_PX,
    end:
      metrics.scrollWidth - metrics.clientWidth - metrics.scrollLeft >
      OVERLAP_THRESHOLD_PX,
  };
}

export function useHorizontalScrollOverlap<
  TViewport extends HTMLElement = HTMLDivElement,
>() {
  const viewportRef = useRef<TViewport>(null);
  const [overlap, setOverlap] = useState<HorizontalScrollOverlap>({
    start: false,
    end: false,
  });

  const measure = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const next = getHorizontalScrollOverlap(viewport);
    setOverlap((current) =>
      current.start === next.start && current.end === next.end ? current : next,
    );
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    measure();
    if (typeof ResizeObserver === "undefined") return;

    const resizeObserver = new ResizeObserver(measure);
    const observeLayout = () => {
      resizeObserver.disconnect();
      resizeObserver.observe(viewport);
      for (const child of viewport.children) resizeObserver.observe(child);
      measure();
    };
    observeLayout();

    if (typeof MutationObserver === "undefined") {
      return () => resizeObserver.disconnect();
    }

    const mutationObserver = new MutationObserver(observeLayout);
    mutationObserver.observe(viewport, { childList: true });

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, [measure]);

  const onScroll = useCallback<UIEventHandler<TViewport>>(
    () => measure(),
    [measure],
  );

  return { overlap, viewportRef, onScroll };
}

type VerticalScrollEndShadowProps = {
  className?: string;
  height?: number;
  opacity?: number;
  style?: CSSProperties;
};

export const VerticalScrollEndShadow = forwardRef<
  HTMLDivElement,
  VerticalScrollEndShadowProps
>(function VerticalScrollEndShadow(
  { className, height = 48, opacity = 0, style },
  ref,
) {
  return (
    <div
      ref={ref}
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-x-0 bottom-0 bg-[linear-gradient(to_top,var(--scroll-shadow-stops))] transition-opacity duration-150",
        className,
      )}
      style={{ ...style, height, opacity }}
    />
  );
});
